// Global
import React from 'react';

// Local
import styles from './NutritionExchanges.module.scss';
import useDictionary from 'lib/hooks/useDictionary';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';

interface INutritionExchangesProps {
  exchangeData: IExchangeItem[];
  carbohydrateChoice: string;
  disclaimer: string;
}

interface IExchangeItem {
  displayName: string;
  quantity: string;
}

export default function NutritionExchanges({
  exchangeData,
  carbohydrateChoice,
  disclaimer,
}: INutritionExchangesProps) {
  // Localization dictionary items
  const { getDictionaryValue } = useDictionary();
  const exchangesHeading = getDictionaryValue(
      'Common.NutritionFacts.Recipe.ExchangesSection.Exchanges'
    ),
    notCalculatedAbbrText = getDictionaryValue(
      'Common.NutritionFacts.Recipe.ExchangesSection.NotCalculated'
    ),
    notCalculatedTipText = getDictionaryValue(
      'Common.NutritionFacts.Recipe.ExchangesSection.NotCalculatedTip'
    ),
    carbohydrateChoiceText = getDictionaryValue(
      'Common.NutritionFacts.Recipe.ExchangesSection.CarbohydrateChoice'
    );

  // Determine if any exchange items have 'nc' quantity to conditionally show footnote
  const showNcFootnote = exchangeData ? exchangeData.some((item) => item.quantity === 'nc') : false;

  return (
    <>
      {exchangeData && (
        <div className={`${styles.nutritionOutOfTableSection}`}>
          <h3 className={`${styles.nutritionOutOfTableHeading}`} id="nutrition-exchanges">
            {exchangesHeading}
          </h3>
          <div className={`${styles.nutritionOutOfTableContent}`}>
            <ul
              className={`${styles.nutritionOutOfTableInlineList}`}
              aria-describedby={'nutrition-exchanges'}
            >
              {/* List items are injected via dangerouslySetInnerHTML above */}
              {exchangeData.length > 0 &&
                exchangeData.map((item: IExchangeItem, index: number) => (
                  <li key={index}>
                    {item.quantity === 'nc' ? (
                      <>
                        {item.displayName}{' '}
                        <abbr title={`${notCalculatedAbbrText}`}>
                          NC&dagger;
                          <span className="screenReaderText">{`, ${notCalculatedTipText}`}</span>
                        </abbr>
                      </>
                    ) : (
                      <>
                        {item.displayName}{' '}
                        <EnhancedRichText
                          value={item.quantity ? item.quantity : ''}
                          parentElementType="span"
                        />
                      </>
                    )}
                    {index === exchangeData.length - 1 ? '' : ','}
                  </li>
                ))}
            </ul>
          </div>
          {showNcFootnote && (
            <div className={`${styles.exchangesDisclaimerTextWrap}`}>
              &dagger;{' '}
              <EnhancedRichText
                value={disclaimer}
                className={styles.exchangesDisclaimerText}
                parentElementType="span"
              />
            </div>
          )}
        </div>
      )}

      {carbohydrateChoice && (
        <div className={`${styles.nutritionOutOfTableSection}`}>
          <h3 className={`${styles.nutritionOutOfTableHeading}`} id="carbohydrate-choice-heading">
            {carbohydrateChoiceText}
          </h3>
          <ul
            aria-describedby={'carbohydrate-choice-heading'}
            className={`${styles.nutritionOutOfTableInlineList}`}
          >
            <li>
              <span className={'screenReaderText'}>{carbohydrateChoiceText}</span>
              <span aria-hidden={'true'}>: </span>
              <EnhancedRichText value={carbohydrateChoice} parentElementType="span" />
            </li>
          </ul>
        </div>
      )}
    </>
  );
}
